import { useEffect, useState } from "react";
import {
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    Divider,
    FormControl,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import BoxAlert from "../../components/boxAlert/index.jsx";
import Header from "../../components/header/index.jsx";
import { Loading } from "../../components/loading";
import { user as loggedAdmin } from "../../classes/user.js";
import useWindowSize from "../../hooks/useWindowSize";
import UserBindingsModal from "./components/userBindingsModal/index.jsx";
import { deleteUser } from "./services/deleteUser.js";
import { getUsers } from "./services/getUsers.js";
import { restoreUser } from "./services/restoreUser.js";
import styles from "./css/users.module.css";

const STATUS_OPTIONS = [
    { value: "all", label: "Todos" },
    { value: "active", label: "Ativos" },
    { value: "deleted", label: "Excluídos" },
];

const headCellSx = {
    fontWeight: 700,
    fontSize: 13,
    color: "#fff",
    backgroundColor: "var(--color-primary-background)",
    whiteSpace: "nowrap",
};

function getInitials(name = "") {
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase();
}

function InfoRow({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" fontWeight={600} noWrap>{value}</Typography>
        </Stack>
    );
}

export default function Users() {
    const [users, setUsers] = useState([]);
    const [usersRender, setUsersRender] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const width = useWindowSize();
    const isDesktop = width >= 1280;

    useEffect(() => {
        const initScreen = async () => {
            try {
                const data = await getUsers();
                setUsers(data);
                setUsersRender(data);
            } catch (e) {
                console.error('ERROR - INIT USERS SCREEN:', e);
            } finally {
                setLoading(false);
            }
        };

        initScreen();
    }, []);

    const applyFilters = (list, searchValue, statusValue) => {
        let filtered = list;

        if (statusValue === "active") {
            filtered = filtered.filter((item) => !item.deleted);
        } else if (statusValue === "deleted") {
            filtered = filtered.filter((item) => item.deleted);
        }

        if (searchValue) {
            const lower = searchValue.toLowerCase();
            filtered = filtered.filter((item) =>
                (item.name && item.name.toLowerCase().includes(lower)) ||
                (item.email && item.email.toLowerCase().includes(lower)) ||
                (item.phone && item.phone.toLowerCase().includes(lower)) ||
                (item.document && item.document.toLowerCase().includes(lower))
            );
        }

        return filtered;
    };

    const onChangeSearch = (value) => {
        setSearch(value);
        setUsersRender(applyFilters(users, value, statusFilter));
    };

    const onChangeStatusFilter = (value) => {
        setStatusFilter(value);
        setUsersRender(applyFilters(users, search, value));
    };

    const handleDeleteUser = async () => {
        try {
            if (!userToDelete?.id) return;

            setDeleting(true);
            setDeleteError(null);

            await deleteUser({ userId: userToDelete.id });

            const markDeleted = (list) => list.map((u) => (
                u.id === userToDelete.id ? { ...u, deleted: true, deleted_at: new Date().toISOString() } : u
            ));

            setUsers((prev) => markDeleted(prev));
            setUsersRender((prev) => markDeleted(prev));
            setUserToDelete(null);

        } catch (error) {
            console.error('ERROR - DELETE USER FUNCTION:', error);
            setDeleteError(error?.response?.data?.error || 'Não foi possível excluir o usuário no momento.');

        } finally {
            setDeleting(false);
        }
    };

    const handleRestoreUser = async (item) => {
        try {
            await restoreUser({ userId: item.id });

            const markRestored = (list) => list.map((u) => (
                u.id === item.id ? { ...u, deleted: false, deleted_at: null } : u
            ));

            setUsers((prev) => markRestored(prev));
            setUsersRender((prev) => markRestored(prev));

        } catch (error) {
            console.error('ERROR - RESTORE USER FUNCTION:', error);
        }
    };

    const formatDocument = (doc) => {
        if (!doc) return '—';
        const d = doc.replace(/\D/g, '');
        if (d.length === 11) {
            return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        return doc;
    };

    const formatPhone = (phone) => {
        if (!phone) return '—';
        const p = phone.replace(/\D/g, '');
        if (p.length === 11) {
            return p.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
        }
        if (p.length === 10) {
            return p.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
        }
        return phone;
    };

    const formatDate = (value) => {
        if (!value) return '—';
        return new Date(value).toLocaleDateString('pt-BR');
    };

    return (
        <div>
            <Header>
                <h2 style={{ color: '#fff' }}>Usuários</h2>

                <Stack direction="row" spacing={1.5} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 130, backgroundColor: "#fff", borderRadius: 1 }}>
                        <Select
                            value={statusFilter}
                            onChange={(e) => onChangeStatusFilter(e.target.value)}
                        >
                            {STATUS_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <div className={styles.searchBar}>
                        <input
                            className={styles.inputSearchStyle}
                            placeholder="Buscar por nome, email, telefone ou CPF..."
                            value={search}
                            onChange={(e) => onChangeSearch(e.target.value)}
                        />
                        {search.length > 0 && (
                            <button
                                className={styles.clearSearchButton}
                                onClick={() => onChangeSearch('')}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </Stack>
            </Header>

            <div className={styles.container}>
                {loading ? (
                    <Loading />
                ) : isDesktop ? (
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{ borderRadius: 3, border: "1px solid #e3e6ea", maxHeight: "79vh" }}
                    >
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx} align="center">Nome</TableCell>
                                    <TableCell sx={headCellSx} align="center">Email</TableCell>
                                    <TableCell sx={headCellSx} align="center">Telefone</TableCell>
                                    <TableCell sx={headCellSx} align="center">CPF</TableCell>
                                    <TableCell sx={headCellSx} align="center">Últ. vínculo</TableCell>
                                    <TableCell sx={headCellSx} align="center">Data de cadastro</TableCell>
                                    <TableCell sx={headCellSx} align="center">Status</TableCell>
                                    <TableCell sx={headCellSx} align="center">Ações</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {usersRender.length > 0 ? (
                                    usersRender.map((item, index) => (
                                        <TableRow
                                            key={item.id ?? index}
                                            hover
                                            sx={{
                                                backgroundColor: index % 2 === 0 ? "#fff" : "#fafbfc",
                                                "&:last-child td": { borderBottom: 0 },
                                            }}
                                        >
                                            <TableCell>
                                                <Stack direction="row" spacing={1.5} alignItems="center">
                                                    <Avatar
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            fontSize: 13,
                                                            bgcolor: "var(--color-primary-background)",
                                                        }}
                                                    >
                                                        {getInitials(item.name)}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={600}>{item.name || "—"}</Typography>
                                                </Stack>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary" align="center">{item.email || "—"}</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2">{formatPhone(item.phone)}</Typography>
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{formatDocument(item.document)}</Typography>
                                            </TableCell>

                                            <TableCell>
                                                {item.last_company_name ? (
                                                    <Box>
                                                        <Typography variant="body2" fontWeight={600}>{item.last_company_name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{formatDate(item.last_linked_at)}</Typography>
                                                    </Box>
                                                ) : (
                                                    <Chip label="Sem vínculo" size="small" variant="outlined" />
                                                )}
                                            </TableCell>

                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">{formatDate(item.created_at)}</Typography>
                                            </TableCell>

                                            <TableCell align="center">
                                                <Chip
                                                    label={item.deleted ? "Excluído" : "Ativo"}
                                                    color={item.deleted ? "error" : "success"}
                                                    size="small"
                                                    variant={item.deleted ? "filled" : "outlined"}
                                                />
                                            </TableCell>

                                            <TableCell align="center">
                                                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" gap={1}>
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() => setSelectedUser(item)}
                                                        sx={{ boxShadow: "none", textTransform: "none", backgroundColor: '#2eb4f1' }}
                                                    >
                                                        Abrir
                                                    </Button>

                                                    {item.deleted ? (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="success"
                                                            onClick={() => handleRestoreUser(item)}
                                                            sx={{ boxShadow: "none", textTransform: "none" }}
                                                        >
                                                            Restaurar
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="error"
                                                            onClick={() => setUserToDelete(item)}
                                                            sx={{ boxShadow: "none", textTransform: "none" }}
                                                        >
                                                            Excluir
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                                Nenhum usuário encontrado.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Stack spacing={2}>
                        {usersRender.length > 0 ? (
                            usersRender.map((item, index) => (
                                <Card key={item.id ?? index} variant="outlined" sx={{ borderRadius: 3, p: 2 }}>
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="flex-start"
                                        spacing={1}
                                        flexWrap="wrap"
                                        sx={{ mb: 1.5, rowGap: 1 }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                                            <Avatar sx={{ bgcolor: "var(--color-primary-background)" }}>
                                                {getInitials(item.name)}
                                            </Avatar>
                                            <Typography variant="subtitle1" fontWeight={700} noWrap>{item.name || "—"}</Typography>
                                        </Stack>

                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="success"
                                                onClick={() => setSelectedUser(item)}
                                                sx={{ boxShadow: "none", textTransform: "none", backgroundColor: '#2eb4f1' }}
                                            >
                                                Abrir
                                            </Button>

                                            {item.deleted ? (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="success"
                                                    onClick={() => handleRestoreUser(item)}
                                                    sx={{ boxShadow: "none", textTransform: "none" }}
                                                >
                                                    Restaurar
                                                </Button>
                                            ) : (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => setUserToDelete(item)}
                                                    sx={{ boxShadow: "none", textTransform: "none" }}
                                                >
                                                    Excluir
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>

                                    <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                                        <Chip
                                            label={item.deleted ? "Excluído" : "Ativo"}
                                            color={item.deleted ? "error" : "success"}
                                            size="small"
                                            variant={item.deleted ? "filled" : "outlined"}
                                        />
                                    </Stack>

                                    <Divider sx={{ my: 1.5 }} />

                                    <Stack spacing={1}>
                                        <InfoRow label="Email" value={item.email || "—"} />
                                        <InfoRow label="Telefone" value={formatPhone(item.phone)} />
                                        <InfoRow label="CPF" value={formatDocument(item.document)} />
                                        <InfoRow label="Cadastro" value={formatDate(item.created_at)} />
                                        <InfoRow
                                            label="Última empresa"
                                            value={
                                                item.last_company_name
                                                    ? `${item.last_company_name} (${formatDate(item.last_linked_at)})`
                                                    : "Sem vínculo"
                                            }
                                        />
                                    </Stack>
                                </Card>
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                                Nenhum usuário encontrado.
                            </Typography>
                        )}
                    </Stack>
                )}
            </div>

            <UserBindingsModal
                show={!!selectedUser}
                user={selectedUser}
                canViewAccess={loggedAdmin.can_view_users_pass}
                canAccessAccount={loggedAdmin.can_view_users_pass} /* QUEM VÊ A SENHA DO USUÁRIO TAMBÉM PODE ACESSAR A CONTA (SESSÃO DE SUPORTE AUDITADA) */
                onClose={() => setSelectedUser(null)}
            />

            <BoxAlert
                show={!!userToDelete && !deleting}
                title="Excluir usuário"
                text={`Tem certeza que deseja excluir o usuário "${userToDelete?.name}" (${userToDelete?.email})?`}
                showButtons={true}
                cancelButtonText="Cancelar"
                confirmButtonText="Excluir"
                confirmFunction={handleDeleteUser}
                onClose={() => setUserToDelete(null)}
            />

            <BoxAlert
                show={!!deleteError}
                title="Erro"
                text={deleteError}
                onClose={() => setDeleteError(null)}
            />
        </div>
    );
}
